import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import SplitType from "split-type";

gsap.registerPlugin(ScrollTrigger);

const pendingAnimations = new WeakMap();
const animationObserver = "IntersectionObserver" in window ? new IntersectionObserver((entries) => {
  entries.forEach((entry) => {
    if (!entry.isIntersecting) return;
    animationObserver.unobserve(entry.target);
    pendingAnimations.get(entry.target)?.forEach((initialize) => initialize());
    pendingAnimations.delete(entry.target);
  });
}, { rootMargin: "200px" }) : null;

function prepareAnimation(element, initialize) {
  if (!animationObserver) {
    initialize();
    return;
  }
  if (!pendingAnimations.has(element)) {
    pendingAnimations.set(element, []);
    animationObserver.observe(element);
  }
  pendingAnimations.get(element).push(initialize);
}

function isInitiallyVisible(element) {
  const bounds = element.getBoundingClientRect();
  return bounds.height > 0 && bounds.top < window.innerHeight && bounds.bottom > 0;
}



/* gsap.from(".image-zoom", {
  scale: 1.1,
  duration: 20, // Animation duration
  ease: "power3.out",
  scrollTrigger: {
    trigger: ".image-zoom", // The parent element triggers the animation
    start: "top 75%", // Animation starts when the parent enters the viewport at 75%
    toggleActions: "play none none none", // Trigger actions: play on enter
  },
}); */

function animateClipText() {
  gsap.utils.toArray(".clip-parent").forEach((parent) => prepareAnimation(parent, () => {
    gsap.to(parent.querySelectorAll(".clip-text"), {
      clipPath: " inset(0 0 0% 0)", // Fully reveal the text
      ease: "power2.inOut", // Add easing for smoother animation
      scrollTrigger: {
        trigger: parent,
        start: "top 70%", // Start earlier for more scroll space
        end: "center center", // End at the center for smoother reveal
        scrub: 2, // Make the animation scrub with the scroll
      },
    });
  }));
}

// Clip-path animation using fromTo to reveal text on scroll

const defaultAnimation = {
  opacity: 0,
  y: "25px",
  duration: 0.6,
  scale: 0.98,
  ease: "power2.out",
  filter: "blur(10px)",
  stagger: {
    amount: 0.3,
    each: 0.15,
    from: "start",
  },
};

function splitAndAnimateText() {
  const splitElements = document.querySelectorAll("[data-split-text], [data-split-text] p");

  if (splitElements.length === 0) return;

  splitElements.forEach((element) => {
    if (isInitiallyVisible(element)) return;
    prepareAnimation(element, () => {
      const splitText = new SplitType(element, { types: "lines" });

      gsap.from(splitText.lines, {
        ...defaultAnimation,
        scrollTrigger: {
          trigger: element,
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: "play none none none",
        },
      });
    });
  });
}

function scaleOnScrub() {
  const elements = document.querySelectorAll("[data-scrub-scale]");

  if (elements.length === 0) return;

  elements.forEach((element) => {
    gsap.to(element, {
      scale: 0.98,
      opacity: 0.1,
      scaleY: 0.8,
      filter: "blur(10px)",
      scrollTrigger: {
        scrub: 1,
        trigger: element,
        start: "bottom 25%", 
        end: "bottom top",
        toggleActions: "play none none none",
      },
    });
  });
}


  // Function to animate individual items
  function animateSingleItems() {
    const singleItems = document.querySelectorAll("[data-animate-item]");

    if (singleItems.length === 0) return;

    singleItems.forEach((item) => {
      if (isInitiallyVisible(item)) return;
      prepareAnimation(item, () => gsap.from(item, {
        ...defaultAnimation,
        scrollTrigger: {
          trigger: item,
          start: "top bottom",
          end: "bottom 20%",
          toggleActions: "play none none none",
        },
      }));
    });
  };

// Function to animate children of elements with a stagger effect
function animateChildrenWithStagger() {
  const staggerContainers = document.querySelectorAll("[data-animate-children]");

  if (staggerContainers.length === 0) return;

  staggerContainers.forEach((container) => {
    const children = Array.from(container.children).filter((child) => !isInitiallyVisible(child));
    if (children.length === 0) return;

    prepareAnimation(container, () => {
      let animatedChildren = children;
      if (container.classList.contains("swiper-wrapper")) {
        const bounds = container.parentElement.getBoundingClientRect();
        const left = Math.max(0, bounds.left);
        const right = Math.min(window.innerWidth, bounds.right);
        animatedChildren = children.filter((child) => {
          const childBounds = child.getBoundingClientRect();
          return childBounds.right > left && childBounds.left < right;
        });
      }
      if (animatedChildren.length === 0) return;

      gsap.from(animatedChildren, {
        ...defaultAnimation,
        stagger: {
          each: 0.1,
          from: "start",
        },
        scrollTrigger: {
          trigger: container,
          start: "top bottom",
          end: "bottom 20%",
          toggleActions: "play none none none",
        },
      });
    });
  });
}

// Let the browser paint the page before preparing scroll animations.
requestAnimationFrame(() => requestAnimationFrame(() => {
  scaleOnScrub();
  animateClipText();
  splitAndAnimateText();
  animateSingleItems();
  animateChildrenWithStagger();
}));
